import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import enTranslations from './en.json';
import trTranslations from './tr.json';
import deTranslations from './de.json';
import frTranslations from './fr.json';
import esTranslations from './es.json';
import ptTranslations from './pt.json';
import ruTranslations from './ru.json';
import arTranslations from './ar.json';
import zhTranslations from './zh.json';
import jaTranslations from './ja.json';
import koTranslations from './ko.json';
import itTranslations from './it.json';
import nlTranslations from './nl.json';
import hiTranslations from './hi.json';

const translations = {
  en: enTranslations,
  tr: trTranslations,
  de: deTranslations,
  fr: frTranslations,
  es: esTranslations,
  pt: ptTranslations,
  ru: ruTranslations,
  ar: arTranslations,
  zh: zhTranslations,
  ja: jaTranslations,
  ko: koTranslations,
  it: itTranslations,
  nl: nlTranslations,
  hi: hiTranslations,
};

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
];

const VALID_CODES = SUPPORTED_LANGUAGES.map(l => l.code);

const LanguageContext = createContext(null);

function detectLanguage() {
  const stored = localStorage.getItem('cv_lang');
  if (stored && VALID_CODES.includes(stored)) return stored;
  // Default to English
  return 'en';
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectLanguage);

  const setLang = useCallback((newLang) => {
    if (VALID_CODES.includes(newLang)) {
      setLangState(newLang);
      localStorage.setItem('cv_lang', newLang);
      document.documentElement.lang = newLang;
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      value = value?.[k];
    }
    if (value === undefined) {
      // Fallback to English
      let fallback = translations.en;
      for (const k of keys) {
        fallback = fallback?.[k];
      }
      return fallback || key;
    }
    return value;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, languages: SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
